// =============================================================================
// io/demo/client.ts — a stand-in for the database, serving the sample book.
// -----------------------------------------------------------------------------
// ── 🚨 WHY THE SEAM IS HERE AND NOT IN THE PROVIDERS ──
//
// The old client keeps everything in one `state` object, so its demo mode swaps
// that object and the whole app follows (app/index.html:8347). This client has
// no such object: it reads from the database in ~20 io modules, some of them per
// tab, and intercepting each one would mean twenty places to forget. Twenty
// places to forget is how a demo ends up showing a trader their REAL book on one
// screen and the sample on another, with nothing on screen to say which.
//
// So the swap happens one layer lower: `useSupabase()` hands out THIS instead of
// the real client while the demo is up, and every read path above it works
// unchanged — including the ones nobody remembered.
//
// ── 🚨 WRITES DO NOT NO-OP. THEY THROW. ──
//
// "Nothing here is saved" is the promise the banner makes, and a silent no-op
// keeps that promise while hiding the bug: a write path that reaches this at all
// is one nobody thought about, and it would fail silently in production forever.
// A throw is loud in development and in the suite, and it cannot corrupt the
// trader's real book either way. ⚠ There is also no code path from here to the
// network — the real client is used ONLY for `auth` and the shared instrument
// catalogue, both read-only, both explained below.
//
// ── ⚠ WHAT THIS IS NOT ──
//
// Not a PostgREST implementation. It supports exactly the operators this app's
// reads use, verified by grep, and throws on anything else rather than quietly
// returning unfiltered rows — a filter silently ignored is a demo that shows
// ninety days of trades on a screen that asked for one.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { buildDemoData, type DemoRow } from '../../domain/demo';

/** What the demo can serve. Anything else reads as genuinely empty. */
const DEMO_TABLES = new Set([
  'trades', 'accounts', 'account_adjustments', 'patterns', 'exit_models',
]);

/**
 * Tables that pass THROUGH to the real database, read-only.
 *
 * ⚠ ONLY the instrument catalogue, and it is not the user's data — it is the
 * shared reference table that says a MES point is $5. Without it the $ / points
 * / ticks / R switch cannot convert, so the demo would show a unit that silently
 * disagrees with its own dollars. Nothing user-scoped is ever passed through.
 */
const PASSTHROUGH_TABLES = new Set(['contracts']);

/** Every read this fake understands. Anything else throws rather than lie. */
type Op = 'eq' | 'neq' | 'in' | 'gte' | 'lte' | 'gt' | 'lt' | 'is';

interface Filter { op: Op; col: string; val: unknown; negate: boolean }

const asNum = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Date.parse(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** One comparison, on one row. Dates arrive as ISO strings and compare fine. */
function matches(row: DemoRow, f: Filter): boolean {
  const v = row[f.col];
  let hit: boolean;
  switch (f.op) {
    case 'eq': hit = String(v) === String(f.val); break;
    case 'neq': hit = String(v) !== String(f.val); break;
    case 'in': hit = (f.val as unknown[]).some((x) => String(x) === String(v)); break;
    case 'is': hit = f.val === null ? v === null || v === undefined : v === f.val; break;
    default: {
      // ⚠ Compare as text when either side is not a number, so an ISO date range
      // works: '2026-08-01' <= '2026-08-15' is true lexically and numerically
      // meaningless. Doing it the other way round silently returns everything.
      const a = asNum(v);
      const b = asNum(f.val);
      const cmp = a !== null && b !== null
        ? (a === b ? 0 : a < b ? -1 : 1)
        : (String(v) === String(f.val) ? 0 : String(v) < String(f.val) ? -1 : 1);
      hit = f.op === 'gte' ? cmp >= 0 : f.op === 'lte' ? cmp <= 0
        : f.op === 'gt' ? cmp > 0 : cmp < 0;
    }
  }
  return f.negate ? !hit : hit;
}

export class DemoWriteError extends Error {
  constructor(what: string) {
    super(
      `Demo mode does not write. Something tried to ${what}, which means a write `
      + 'path reached the demo client — nothing is saved here, so this is a bug '
      + 'to fix rather than a message to show a user.',
    );
    this.name = 'DemoWriteError';
  }
}

/**
 * A query being built. Thenable, so `await sb.from(x).select(y).eq(...)` works
 * exactly as it does against the real client.
 */
class DemoQuery implements PromiseLike<{ data: DemoRow[] | DemoRow | null; error: null }> {
  private filters: Filter[] = [];
  private sort: { col: string; asc: boolean }[] = [];
  private from = 0;
  private to = Infinity;
  private one: 'single' | 'maybe' | null = null;

  constructor(private rows: DemoRow[]) {}

  private add(op: Op, col: string, val: unknown, negate = false): this {
    this.filters.push({ op, col, val, negate });
    return this;
  }

  eq(col: string, val: unknown): this { return this.add('eq', col, val); }
  neq(col: string, val: unknown): this { return this.add('neq', col, val); }
  in(col: string, val: unknown[]): this { return this.add('in', col, val); }
  gte(col: string, val: unknown): this { return this.add('gte', col, val); }
  lte(col: string, val: unknown): this { return this.add('lte', col, val); }
  gt(col: string, val: unknown): this { return this.add('gt', col, val); }
  lt(col: string, val: unknown): this { return this.add('lt', col, val); }
  is(col: string, val: unknown): this { return this.add('is', col, val); }
  not(col: string, op: string, val: unknown): this {
    return this.add(op as Op, col, val, true);
  }

  /** ⚠ Projection is IGNORED — extra columns are harmless, missing ones are not. */
  select(): this { return this; }

  order(col: string, opts?: { ascending?: boolean }): this {
    this.sort.push({ col, asc: opts?.ascending !== false });
    return this;
  }

  limit(n: number): this { this.to = this.from + n - 1; return this; }
  range(from: number, to: number): this { this.from = from; this.to = to; return this; }
  single(): this { this.one = 'single'; return this; }
  maybeSingle(): this { this.one = 'maybe'; return this; }

  /** ⚠ Any write reaching a read builder is a bug — see the header. */
  insert(): never { throw new DemoWriteError('insert'); }
  update(): never { throw new DemoWriteError('update'); }
  upsert(): never { throw new DemoWriteError('upsert'); }
  delete(): never { throw new DemoWriteError('delete'); }

  private run(): DemoRow[] | DemoRow | null {
    let out = this.rows.filter((r) => this.filters.every((f) => matches(r, f)));
    for (const s of [...this.sort].reverse()) {
      out = [...out].sort((a, b) => {
        const x = a[s.col]; const y = b[s.col];
        const an = asNum(x); const bn = asNum(y);
        const c = an !== null && bn !== null
          ? an - bn
          : String(x) < String(y) ? -1 : String(x) > String(y) ? 1 : 0;
        return s.asc ? c : -c;
      });
    }
    const page = out.slice(this.from, this.to === Infinity ? undefined : this.to + 1);
    if (this.one) return page[0] ?? null;
    return page;
  }

  then<R1 = { data: DemoRow[] | DemoRow | null; error: null }, R2 = never>(
    onfulfilled?: ((v: { data: DemoRow[] | DemoRow | null; error: null }) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((r: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return Promise.resolve({ data: this.run(), error: null }).then(onfulfilled, onrejected);
  }
}

/**
 * The client the app uses while the demo is up.
 *
 * ⚠ `userId` STAMPS EVERY ROW. Almost every read carries `.eq('user_id', …)` —
 * that is how RLS-scoped reads are written here — and rows without it would be
 * filtered to nothing by the app's own queries. The demo book would load, and
 * every screen would show empty.
 */
export function demoClient(
  real: SupabaseClient, opts: { today: string; userId: string },
): SupabaseClient {
  const d = buildDemoData(opts.today);
  const stamp = (rows: DemoRow[]): DemoRow[] =>
    rows.map((r) => ({ ...r, user_id: opts.userId }));

  const tables: Record<string, DemoRow[]> = {
    trades: stamp(d.trades),
    accounts: stamp(d.accounts),
    account_adjustments: stamp(d.adjustments),
    patterns: stamp(d.patterns),
    exit_models: stamp(d.exitModels),
  };

  return {
    from(table: string) {
      if (PASSTHROUGH_TABLES.has(table)) return real.from(table);
      // ⚠ An unknown table reads EMPTY rather than throwing. The demo book has
      // no goals, no journal entries and no backtests, and that is a true
      // answer — those screens show their own empty states, which is what a
      // prospect should see.
      return new DemoQuery(DEMO_TABLES.has(table) ? (tables[table] ?? []) : []);
    },
    // ⚠ Identity is still the real user's — the demo swaps their DATA, not who
    // they are. Reading it never leaves the browser (the session is cached), and
    // signing out from inside a demo has to keep working.
    auth: real.auth,
    // Sample trades carry no screenshots, so nothing reads; an upload would be a
    // write, and writes throw.
    storage: {
      from() {
        return {
          async list() { return { data: [], error: null }; },
          async download() { throw new DemoWriteError('download a file'); },
          async upload() { throw new DemoWriteError('upload a file'); },
          async remove() { throw new DemoWriteError('remove a file'); },
          getPublicUrl() { return { data: { publicUrl: '' } }; },
        };
      },
    },
    rpc(): never { throw new DemoWriteError('call an RPC'); },
    functions: {
      invoke(): never { throw new DemoWriteError('invoke an Edge Function'); },
    },
  } as unknown as SupabaseClient;
}
